import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { McpEditor } from "@/components/mcp/mcp-editor";
import { useTenant } from "@/context/tenant-context";
import { mcpApi, type McpCatalogueItem } from "@/api/mcp";
import { queryKeys } from "@/lib/query-keys";
import {
  Puzzle, Plus, Trash2, Power, Wrench, Globe, Terminal, Radio,
  ChevronRight, Pencil, Building2, Cloud,
} from "lucide-react";

const transportIcons: Record<string, typeof Globe> = {
  http: Globe, sse: Radio, stdio: Terminal,
};

export function TenantMcpList({
  startInCreate = false,
  onOpenMcp,
}: {
  /** Embedded hosts (the settings dialog) open the MCP in place instead of
   *  navigating to the full `/settings/mcp/:id` page. */
  onOpenMcp?: (tenantMcpId: string) => void;
  /**
   * Open straight on the create form. Used by the Add MCP button on an agent's
   * Config tab, so somebody assigning MCPs to an agent can create the missing
   * one without first finding this screen.
   */
  startInCreate?: boolean;
} = {}) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<"list" | "create" | "edit">(
    startInCreate ? "create" : "list",
  );
  const [editingItem, setEditingItem] = useState<McpCatalogueItem | null>(null);

  // Unified catalogue view: platform-wide MCPs + this tenant's own.
  const { data: catalogue = [] } = useQuery({
    queryKey: queryKeys.mcpCatalogue.filtered(tenantId),
    queryFn: () => mcpApi.listCatalogue({ companyId: tenantId }),
    enabled: !!tenantId,
  });

  // Which catalogue entries are currently "assigned" to this tenant (their
  // enabled/tool-filter state lives here).
  const { data: tenantMcps = [] } = useQuery({
    queryKey: queryKeys.tenantMcps.byCompany(tenantId),
    queryFn: () => mcpApi.listTenantMcps(tenantId),
    enabled: !!tenantId,
  });

  const assignmentByCatalogueId = useMemo(() => {
    const map = new Map<string, (typeof tenantMcps)[number]>();
    for (const tm of tenantMcps) {
      if (tm.catalogueMcpId) map.set(tm.catalogueMcpId, tm);
    }
    return map;
  }, [tenantMcps]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["mcp-catalogue"] });
    queryClient.invalidateQueries({ queryKey: queryKeys.tenantMcps.byCompany(tenantId) });
  };

  const assignMutation = useMutation({
    mutationFn: (item: McpCatalogueItem) =>
      mcpApi.assignTenantMcp(tenantId, {
        companyId: tenantId,
        catalogueMcpId: item.id,
        name: item.name,
      }),
    onSuccess: invalidateAll,
  });

  const unassignMutation = useMutation({
    mutationFn: (tenantMcpId: string) => mcpApi.removeTenantMcp(tenantId, tenantMcpId),
    onSuccess: invalidateAll,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ tenantMcpId, enabled }: { tenantMcpId: string; enabled: boolean }) =>
      mcpApi.updateTenantMcp(tenantId, tenantMcpId, { enabled }),
    onSuccess: invalidateAll,
  });

  const deleteCatalogueMutation = useMutation({
    mutationFn: (catalogueId: string) => mcpApi.deleteCatalogue(catalogueId),
    onSuccess: invalidateAll,
  });

  if (mode !== "list") {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={() => { setMode("list"); setEditingItem(null); }}>
          ← Back to MCPs
        </Button>
        <McpEditor
          scope={{ kind: "tenant", tenantId }}
          initialValue={editingItem ?? undefined}
          onSaved={() => {
            invalidateAll();
            setMode("list");
            setEditingItem(null);
          }}
          onCancel={() => {
            setMode("list");
            setEditingItem(null);
          }}
        />
      </div>
    );
  }

  return (
    <Card data-tour="mcp-list">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <Puzzle size={18} />
          MCP Servers ({catalogue.length})
        </CardTitle>
        <Button size="sm" data-tour="mcp-create" onClick={() => { setEditingItem(null); setMode("create"); }}>
          <Plus size={14} className="mr-1.5" />
          Create MCP
        </Button>
      </CardHeader>
      <CardContent>
        {catalogue.length === 0 ? (
          <p className="text-sm text-text-muted">
            No MCPs available. Click "Create MCP" to add one for this tenant, or ask your platform admin to publish one.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {catalogue.map((item) => {
              const isTenantOwned = item.companyId === tenantId;
              const assignment = assignmentByCatalogueId.get(item.id);
              const availableTools = item.toolSchemaCache ? Object.keys(item.toolSchemaCache) : [];
              const Icon = transportIcons[item.transportType] || Puzzle;
              const hasToolRestriction =
                assignment?.allowedTools !== null && assignment?.allowedTools !== undefined;

              return (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded border border-border px-3 py-2.5 transition-colors hover:bg-surface-hover"
                >
                  <div
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
                    onClick={() => {
                      if (!assignment) return;
                      if (onOpenMcp) onOpenMcp(assignment.id);
                      else navigate(`/tenants/${tenantId}/settings/mcp/${assignment.id}`);
                    }}
                  >
                    <Icon size={14} className="text-accent shrink-0" />
                    <span className="text-sm font-medium text-text-primary truncate">
                      {item.displayName || item.name}
                    </span>
                    <span className="text-xs text-text-muted">
                      ({item.transportType.toUpperCase()})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={isTenantOwned ? "warning" : "secondary"}>
                      {isTenantOwned ? <Building2 size={10} className="mr-1" /> : <Cloud size={10} className="mr-1" />}
                      {isTenantOwned ? "Tenant" : "Platform"}
                    </Badge>
                    {assignment && availableTools.length > 0 && (
                      <Badge variant="secondary">
                        <Wrench size={10} className="mr-1" />
                        {hasToolRestriction
                          ? `${assignment.allowedTools!.length}/${availableTools.length}`
                          : `${availableTools.length}/${availableTools.length}`}{" "}
                        tools
                      </Badge>
                    )}
                    {assignment ? (
                      <>
                        <Badge variant={assignment.enabled ? "success" : "default"}>
                          {assignment.enabled ? "enabled" : "disabled"}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          title={assignment.enabled ? "Disable" : "Enable"}
                          onClick={() =>
                            toggleMutation.mutate({
                              tenantMcpId: assignment.id,
                              enabled: !assignment.enabled,
                            })
                          }
                        >
                          <Power size={14} />
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => assignMutation.mutate(item)}
                        disabled={assignMutation.isPending}
                      >
                        <Plus size={12} className="mr-1" />
                        Enable
                      </Button>
                    )}
                    {isTenantOwned ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Edit"
                          onClick={() => {
                            setEditingItem(item);
                            setMode("edit");
                          }}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-600"
                          title="Delete this MCP (cannot be undone)"
                          onClick={() => {
                            if (confirm(`Delete "${item.displayName || item.name}"? This also removes it from any agents using it.`)) {
                              deleteCatalogueMutation.mutate(item.id);
                            }
                          }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </>
                    ) : (
                      assignment && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Remove from tenant"
                          onClick={() => unassignMutation.mutate(assignment.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )
                    )}
                    {assignment && <ChevronRight size={14} className="text-text-muted" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
